'use client'

import { useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import {
  dismissInstallOffer,
  getInstallPrompt,
  isInstallOfferDismissed,
  isIos,
  isStandalone,
  onInstallPromptChange,
  setInstallPrompt,
} from '@/lib/pwa'

// Яркая кнопка "Установить приложение" на дашборде и в шапке лендинга
// (пользователь 2026-09-25). Показываем только там, где установить можно:
// - браузер прислал beforeinstallprompt (Chrome/Edge/Яндекс на Android и
//   компьютере) - установка в одно нажатие; после неё браузер событие больше
//   не шлёт, и кнопка пропадает сама;
// - iPhone/iPad - окошко с шагами через "Поделиться"; Safari не сообщает, что
//   сайт уже на экране "Домой", поэтому там есть "Я установил" и крестик.
// В самом установленном приложении кнопки нет. Остальным браузерам - только
// инструкция в настройках (components/install-app-panel.tsx).

type Offer = 'prompt' | 'ios' | null

function detectOffer(): Offer {
  if (isStandalone() || isInstallOfferDismissed()) return null
  if (getInstallPrompt()) return 'prompt'
  if (isIos()) return 'ios'
  return null
}

// Экспорт: тот же значок и окошко для iPhone в блоке лендинга (install-app-cta.tsx).
export function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
      <path d="M12 4v11M7 10l5 5 5-5" />
      <path d="M5 20h14" />
    </svg>
  )
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: '-3px' }}>
      <path d="M12 3v12M8 7l4-4 4 4" />
      <path d="M6 11H5v10h14V11h-1" />
    </svg>
  )
}

// Через портал в body: шапка лендинга с backdrop-blur становится опорой для
// position: fixed, и окошко без портала съезжало внутрь шапки.
export function IosSheet({ onClose }: { onClose: () => void }) {
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Установить Turkido"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'oklch(0.08 0.02 254 / 0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="tk-dash-card"
        style={{ width: '100%', maxWidth: 420, borderRadius: 20, padding: 22, marginBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="font-display" style={{ fontSize: 18, fontWeight: 700 }}>
          Turkido на экране &quot;Домой&quot;
        </div>
        <p style={{ fontSize: 15, lineHeight: 1.5, marginTop: 8, color: 'var(--tk-fg-muted)' }}>
          Приложение будет открываться с иконки, без адресной строки.
        </p>
        <ol style={{ fontSize: 15, lineHeight: 1.55, marginTop: 12, paddingLeft: 20, listStyle: 'decimal', display: 'grid', gap: 6 }}>
          <li>
            Нажмите &quot;Поделиться&quot; <ShareIcon /> внизу экрана Safari.
          </li>
          <li>Выберите пункт На экран &quot;Домой&quot;.</li>
          <li>Нажмите &quot;Добавить&quot;.</li>
        </ol>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
          <button
            type="button"
            onClick={() => {
              dismissInstallOffer()
              onClose()
            }}
            className="tk-glass-btn tk-glass-coal rounded-2xl px-4 py-2.5 text-sm font-bold"
          >
            Я установил
          </button>
          <button type="button" onClick={onClose} className="tk-pill">
            Позже
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export function InstallAppButton({ variant }: { variant: 'bar' | 'pill' }) {
  const offer = useSyncExternalStore<Offer>(onInstallPromptChange, detectOffer, () => null)
  const [sheet, setSheet] = useState(false)
  if (!offer) return sheet ? <IosSheet onClose={() => setSheet(false)} /> : null

  const install = async () => {
    if (offer === 'ios') {
      setSheet(true)
      return
    }
    const e = getInstallPrompt()
    if (!e) return
    await e.prompt()
    await e.userChoice
    // Событие одноразовое; если установили, браузер пришлёт appinstalled.
    setInstallPrompt(null)
  }

  const button = (
    <div className={variant === 'bar' ? 'flex w-full items-center gap-2 sm:justify-end' : 'flex items-center gap-2'}>
      <button
        type="button"
        onClick={install}
        className={`tk-glass-btn tk-glass-coal inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-bold whitespace-nowrap${variant === 'bar' ? ' flex-1 sm:flex-none' : ''}`}
      >
        <DownloadIcon />
        Установить приложение
      </button>
      <button
        type="button"
        onClick={dismissInstallOffer}
        aria-label="Не показывать кнопку установки"
        className="tk-back-btn shrink-0"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="13" height="13" aria-hidden="true">
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      </button>
    </div>
  )

  return (
    <>
      {variant === 'bar' ? <div className="mx-auto w-full max-w-[1180px] px-4 pb-3 sm:px-8">{button}</div> : button}
      {sheet && <IosSheet onClose={() => setSheet(false)} />}
    </>
  )
}
